// frontend/src/pages/admin/reports/SalesReport.js
import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiShoppingCart,
  FiTrendingUp,
  FiDollarSign,
  FiPackage,
  FiCalendar,
  FiUsers
} from 'react-icons/fi';

const SalesReport = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Date filters - Default to current month
  const [dateRange, setDateRange] = useState('month');
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (dateRange !== 'custom') {
      handleDateRangeChange(dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    let start, end;

    switch (range) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        start = monthStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'custom':
        return;
      default:
        start = end = today.toISOString().split('T')[0];
    }

    setStartDate(start);
    setEndDate(end);
  };

  const fetchReport = async (start, end) => {
    setLoading(true);
    try {
      // Fetch summary
      const { data: summaryData } = await axios.get('/api/reports/sales/summary', {
        params: { start_date: start, end_date: end }
      });

      if (summaryData.success) {
        setSummary(summaryData.data.summary);
        setPaymentMethods(summaryData.data.payment_methods);
      }

      // Fetch top products
      const { data: productsData } = await axios.get('/api/reports/sales/top-products', {
        params: { start_date: start, end_date: end, limit: 10 }
      });

      if (productsData.success) {
        setTopProducts(productsData.data.products);
      }

    } catch (error) {
      console.error('Fetch report error:', error);
      toast.error('Тайлан татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSearch = () => {
    if (!startDate || !endDate) {
      toast.warning('Эхлэх болон дуусах огноо сонгоно уу');
      return;
    }
    fetchReport(startDate, endDate);
  };

  const paymentMethodLabels = {
    cash: 'Бэлэн мөнгө',
    card: 'Карт',
    transfer: 'Шилжүүлэг',
    credit: 'Зээл'
  };

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">Хугацаа сонгох</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <button
            onClick={() => handleDateRangeChange('today')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'today'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Өнөөдөр
          </button>
          <button
            onClick={() => handleDateRangeChange('yesterday')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'yesterday'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Өчигдөр
          </button>
          <button
            onClick={() => handleDateRangeChange('week')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'week'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            7 хоног
          </button>
          <button
            onClick={() => handleDateRangeChange('month')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'month'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Энэ сар
          </button>
          <button
            onClick={() => setDateRange('custom')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              dateRange === 'custom'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Сонгох
          </button>
        </div>

        {dateRange === 'custom' && (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Эхлэх огноо
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дуусах огноо
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input"
              />
            </div>
            <button
              onClick={handleCustomDateSearch}
              className="btn btn-primary"
            >
              Хайх
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner-lg mx-auto mb-4"></div>
          <p className="text-gray-600">Тайлан бэлтгэж байна...</p>
        </div>
      ) : summary ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Invoices */}
            <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-semibold">Нийт борлуулалт</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {summary.total_invoices || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <FiShoppingCart className="text-blue-700" size={24} />
                </div>
              </div>
            </div>

            {/* Total Customers */}
            <div className="card bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-semibold">Харилцагчид</p>
                  <p className="text-2xl font-bold text-purple-900 mt-1">
                    {summary.total_customers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                  <FiUsers className="text-purple-700" size={24} />
                </div>
              </div>
            </div>

            {/* Total Sales */}
            <div className="card bg-gradient-to-br from-green-50 to-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-semibold">Нийт дүн</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {(summary.total_sales || 0).toLocaleString()}₮
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                  <FiDollarSign className="text-green-700" size={24} />
                </div>
              </div>
            </div>

            {/* Average Order - Fixed to 2 decimal places */}
            <div className="card bg-gradient-to-br from-orange-50 to-orange-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-semibold">Дундаж дүн</p>
                  <p className="text-2xl font-bold text-orange-900 mt-1">
                    {(parseFloat(summary.average_order_value) || 0).toLocaleString('mn-MN', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}₮
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
                  <FiTrendingUp className="text-orange-700" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">💳 Төлбөрийн хэлбэрээр</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {paymentMethods.map((method, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 font-medium">
                    {paymentMethodLabels[method.payment_method] || method.payment_method}
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {method.count} удаа
                  </p>
                  <p className="text-sm text-green-600 font-semibold mt-1">
                    {method.total.toLocaleString()}₮
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {((method.count / summary.total_invoices) * 100).toFixed(1)}%
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Products Table */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">🏆 Топ 10 бараа</h3>

            {topProducts.length === 0 ? (
              <div className="text-center py-12">
                <FiPackage size={64} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600">Мэдээлэл олдсонгүй</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">№</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Бараа</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Тоо ширхэг</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Нийт борлуулалт</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Борлуулалт</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topProducts.map((product, index) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{index + 1}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{product.product_name}</p>
                            <p className="text-xs text-gray-500">{product.barcode}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                            {product.quantity_sold}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-sm font-semibold text-green-600">
                            {product.total_sales.toLocaleString()}₮
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-600">
                            {product.order_count} удаа
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600">Хугацаа сонгоно уу</p>
        </div>
      )}
    </div>
  );
};

export default SalesReport;